import { useEffect, useState } from "react";
import { getAllAssignedEmployeesLeaves, getMyAssignedEmployees } from "api/apiHelper";
import type { EmployeeLeavesDetailsType } from "types/leaves-types";
import { LeaveDetailsType } from "pages/leaves/Leaves";
import Backdrop from "components/Backdrop";
import ErrorBox from "components/ErrorBox";
import UnifiedLeavesTable from "features/leaves/UnifiedLeavesTable";
import Card from "components/Card";

const ClientLeaves = () => {
  const [pendingLeaves, setPendingLeaves] = useState<LeaveDetailsType[]>([]);
  const [approvedLeaves, setApprovedLeaves] = useState<LeaveDetailsType[]>([]);
  const [declinedLeaves, setDeclinedLeaves] = useState<LeaveDetailsType[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalAssignedEmployees, setTotalAssignedEmployees] = useState<number>(0);
  const [employeesOnLeave, setEmployeesOnLeave] = useState<number>(0);

  const fetchLeaves = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch all leaves using paginated endpoint (fetch all pages)
      let allLeaves: EmployeeLeavesDetailsType[] = [];
      let nextPageUrl: string | null = null;
      let pageCount = 0;
      const maxPages = 100; // Safety limit
      
      do {
        const response = nextPageUrl 
          ? await getAllAssignedEmployeesLeaves(undefined, nextPageUrl)
          : await getAllAssignedEmployeesLeaves();
        
        if (response.data?.results) {
          allLeaves = [...allLeaves, ...response.data.results];
          nextPageUrl = response.data.next;
        } else if (Array.isArray(response.data)) {
          allLeaves = [...allLeaves, ...response.data];
          nextPageUrl = null;
        } else {
          break;
        }
        
        pageCount++;
      } while (nextPageUrl && pageCount < maxPages);
      
      // Filter to show only Manager Approved (MA) leaves for client users
      const managerApproved = allLeaves.filter(leave => leave.leave_status === 'MA') as EmployeeLeavesDetailsType[];
      // For client users: CA goes to approved, CR goes to declined
      const approved = allLeaves.filter(leave => leave.leave_status === 'A' || leave.leave_status === 'CA') as EmployeeLeavesDetailsType[];
      const declined = allLeaves.filter(leave => leave.leave_status === 'D' || leave.leave_status === 'MD' || leave.leave_status === 'CR') as EmployeeLeavesDetailsType[];
      
      setPendingLeaves(managerApproved as LeaveDetailsType[]);
      setApprovedLeaves(approved as LeaveDetailsType[]);
      setDeclinedLeaves(declined as LeaveDetailsType[]);
      
      // Calculate employees on leave (approved leaves with current date in range)
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const onLeave = approved.filter(leave => {
        const startDate = new Date(leave.start_date);
        const endDate = new Date(leave.end_date);
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(0, 0, 0, 0);
        return today >= startDate && today <= endDate;
      });
      setEmployeesOnLeave(onLeave.length);
      
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message || "Failed to load leave requests.");
    } finally {
      setLoading(false);
    }
  };

  const fetchAssignedEmployeesCount = async () => {
    try {
      const response = await getMyAssignedEmployees('', 1000);
      const assignments = response.data?.results || response.data || [];
      setTotalAssignedEmployees(Array.isArray(assignments) ? assignments.length : 0);
    } catch (err: any) {
      console.error("Failed to fetch assigned employees count:", err);
    }
  };

  useEffect(() => {
    fetchLeaves();
    fetchAssignedEmployeesCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading && pendingLeaves.length === 0 && approvedLeaves.length === 0 && declinedLeaves.length === 0) {
    return <Backdrop show={true} />;
  }

  return (
    <div className="mt-5">
      {error && <ErrorBox errorMessage={error} />}

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {/* Total Pending Leaves */}
        <Card extra="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Manager Approved Leaves</p>
              <p className="text-2xl font-bold text-navy-700 dark:text-white">
                {pendingLeaves.length}
              </p>
            </div>
            <div className="w-12 h-12 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center">
              <i className="fas fa-clock text-yellow-600 dark:text-yellow-400 text-xl"></i>
            </div>
          </div>
        </Card>

        {/* Total Assigned Employees */}
        <Card extra="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Assigned Employees</p>
              <p className="text-2xl font-bold text-navy-700 dark:text-white">
                {totalAssignedEmployees}
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
              <i className="fas fa-users text-blue-600 dark:text-blue-400 text-xl"></i>
            </div>
          </div>
        </Card>

        {/* Employees Currently on Leave */}
        <Card extra="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Employees on Leave</p>
              <p className="text-2xl font-bold text-navy-700 dark:text-white">
                {employeesOnLeave}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
              <i className="fas fa-calendar-check text-green-600 dark:text-green-400 text-xl"></i>
            </div>
          </div>
        </Card>
      </div>

      {/* Unified Leaves Table */}
      <UnifiedLeavesTable
        pendingData={pendingLeaves}
        approvedData={approvedLeaves}
        declinedData={declinedLeaves}
        isManager={false}
      />
    </div>
  );
};

export default ClientLeaves;
